import { I18N_NAMESPACE } from "@/config/i18n";
import { depositEligibleBonusPlansRequest } from "@/fe-core/_redux/actions/userActions";
import {
  closeProcessDepositModal,
  processDepositRequest,
  resetProcessDepositStatus,
} from "@/fe-core/_redux/actions/userActions/cashierActions/processDepositActions";
import { isUserAuthenticatedSelector } from "@/fe-core/_redux/selectors/sessionSelectors";
import { userBalanceDataSelector } from "@/fe-core/_redux/selectors/userSelectors";
import {
  paymentMethodSelector,
  processDepositPendingSelector,
  processDepositRedirectSelector,
  processDepositStatusSelector,
} from "@/fe-core/_redux/selectors/userSelectors/cashierSelectors/processDepositSelectors";
import {
  OK_STATUS,
  REDIRECT_STATUS,
  validateProcessDepositAmount,
} from "@/fe-core/helpers/general";
import {
  DEFAULT_CURRENCY,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DEPOSIT_AMOUNTS,
  DEFAULT_DEPOSIT_INPUT_AMOUNT,
  storageKeys,
} from "@/config/general";

import { IStatus } from "@/fe-core/meta/interfaces/root";
import { depositPaymentMethodTypes } from "@/fe-core/meta/types/user/cashier/processDeposit";
import { useFormatter, useTranslations } from "next-intl";
import { FormEvent, memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "../Button";
import Cashier from "../Cashier";
import DepositBonuses from "./DepositBonuses";

const ProcessDepositModal = () => {
  const dispatch = useDispatch();
  const format = useFormatter();
  const t = useTranslations(I18N_NAMESPACE.DEPOSIT);

  const [amount, setAmount] = useState<number>();
  const [redirect, setRedirect] = useState<string | null>(null);
  const [amountErr, setAmountErr] = useState<string | null>(null);
  const [selectedBonusPlanId, setSelectedBonusPlanId] = useState<number | null>(
    null
  );

  const selectedPaymentMethod = useSelector(paymentMethodSelector);
  const processDepositPending = useSelector(processDepositPendingSelector);
  const processDepositStatus = useSelector(processDepositStatusSelector);
  const processDepositRedirect = useSelector(processDepositRedirectSelector);
  const isUserAuthenticated = useSelector(isUserAuthenticatedSelector);

  const { totalCurrency: currency } =
    useSelector(userBalanceDataSelector) || {};

  useEffect(() => {
    dispatch(resetProcessDepositStatus());
    setRedirect(null);
  }, []);

  useEffect(() => {
    if (processDepositStatus) {
      if (processDepositStatus === REDIRECT_STATUS) {
        setRedirect(processDepositRedirect?.authToken);
      } else if (processDepositStatus === OK_STATUS) {
        toast.success(t("deposit-processed"));
        closeModal();
      } else {
        console.log("processDepositStatus", processDepositStatus);

        switch((processDepositStatus as IStatus)?.status) {
          case 'HIGH_LIMIT':
            // @todo
            toast.error('Accumulated depost limit reached try again in 30min. If you are unable to make a new deposit after 30mins, please contact support.');
            break;

          default:
            toast.error((processDepositStatus as IStatus)?.message);
        }


      }
    }
    return () => setRedirect(null);
  }, [processDepositStatus]);

  const closeModal = () => {
    dispatch(closeProcessDepositModal());
    setRedirect(null);
    setAmount(undefined);
    setSelectedBonusPlanId(null);
  };

  useEffect(() => {
    if (isUserAuthenticated && selectedPaymentMethod) {
      dispatch(
        depositEligibleBonusPlansRequest({
          depositAmount: amount as number,
          paymentMethod: selectedPaymentMethod,
        })
      );
    }
  }, [isUserAuthenticated, amount, selectedPaymentMethod]);

  // useEffect(() => {
  //   setAmountErr(validateProcessDepositAmount(amount, t));
  // }, [amount, t]);

  const onAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const amount = +event?.target?.value;
    setAmount(amount);
    setAmountErr(validateProcessDepositAmount(amount, t));
  };

  const onAmountBlur = () => {
    setAmountErr(validateProcessDepositAmount(amount, t));
  };

  const processDeposit = (e: FormEvent) => {
    e.preventDefault();
    const amountErr = validateProcessDepositAmount(amount, t);
    setAmountErr(amountErr);

    if (amountErr || !amount) return;

    dispatch(
      processDepositRequest({
        amount: amount as number,
        paymentMethod: selectedPaymentMethod as depositPaymentMethodTypes,
        type: "DEPOSIT",
        requestedBonusPlanId: selectedBonusPlanId || undefined,
      })
    );
  };

  const quickSelectAmount = (
    e: React.MouseEvent<HTMLButtonElement>,
    amount: number
  ) => {
    e.preventDefault();
    setAmount(amount);
    setAmountErr(validateProcessDepositAmount(amount, t));
  };

  return (
    <div>
      <Cashier authToken={redirect} />
      {!!selectedPaymentMethod &&

        <div>
          {redirect && !processDepositPending ? (
            // <Cashier authToken={redirect} />
              <></>
          ) : (
            <>
              {/* <h4 className="m-0 text-xl font-bold sm:text-base text-tertiary dark:text-white">
                {t("deposit-modal-header")}
              </h4> */}
              {selectedPaymentMethod === depositPaymentMethodTypes.OMEGA_BANK ||
                selectedPaymentMethod === depositPaymentMethodTypes.PRAXIS_ALL ? (

                <form className="w-full max-w-md" onSubmit={processDeposit}>
                  <input
                    className="font-bold form-input placeholder:font-normal"
                    type="number"
                    onChange={onAmountChange}
                    onBlur={onAmountBlur}
                    value={amount}
                    placeholder={t("amount-placeholder")}
                    disabled={processDepositPending}
                  />
                  {amountErr && (
                    <div className="m-0 mt-0.5 text-[10px] font-medium text-error dark:text-error-500">
                      {amountErr}
                    </div>
                  )}

                  {currency && <div className="grid grid-cols-4 gap-2 my-4">
                    {(DEFAULT_DEPOSIT_AMOUNTS[currency?.toUpperCase()] ||
                        DEFAULT_DEPOSIT_AMOUNTS["default"]).map((amount, index) => {
                      // const suggestedAmount =
                      //     suggestedAmounts?.[
                      //         `button${index + 1}` as keyof typeof suggestedAmounts
                      //         ] || amount;

                      const suggestedAmount =
                          amount;
                      return (
                          <button
                              className="btn btn--theme btn--sm"
                              onClick={(e) => quickSelectAmount(e, suggestedAmount)}
                              disabled={processDepositPending}
                              key={amount}
                          >
                      <span>
                        {format.number(suggestedAmount, {
                          style: "currency",
                          currency,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                          </button>

                    //       <button
                    //           className="btn btn--sm bg-primary-600 !text-base hover:bg-primary-700"
                    //           onClick={(e) => quickSelectAmount(e, suggestedAmount)}
                    //           disabled={processDepositPending}
                    //           key={amount}
                    //       >
                    // <span>
                    //   {format.number(suggestedAmount, {
                    //     style: "currency",
                    //     currency: profileData?.currency || DEFAULT_CURRENCY,
                    //     minimumFractionDigits: 0,
                    //     maximumFractionDigits: 2,
                    //   })}
                    // </span>
                    //       </button>
                      );
                    })}
                  </div>}

                  <Button
                    label={t("process-deposit-btn-label")}
                    disabled={!!amountErr || !amount || processDepositPending}
                    loading={processDepositPending}
                    buttonType="submit"
                    className="mt-5"
                  />

                  {amount !== undefined && amount > 0 && (
                    <div className="space-y-3 mt-3 mb-5">
                      <DepositBonuses
                        setSelectedBonusPlanId={setSelectedBonusPlanId}
                        selectedBonusPlanId={selectedBonusPlanId}
                      />
                    </div>
                  )}
                </form>
              ) : (
                <></>
              )}
            </>
          )}
        </div>
      }
    </div>
  );
};

export default memo(ProcessDepositModal);
